import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getAuth, signOut } from 'firebase/auth';

import { RootState } from '../store';
import {
  clearConversation,
  clearSpeechRateAlert,
  addTranscript,
  updateSpeechRateAnalysis,
  addQuestion,
  addAnswer,
  addStoredAnswer,
} from '../store/conversationSlice';
import { logoutUser } from '../store/authSlice';
import {
  fetchIndustries,
  fetchRoleData,
  setSelectedIndustry,
  setSelectedRole,
  toggleQuestionAsked,
  togglePitchChecked,
  clearSalesPlaybookSession,
} from '../store/slices/salesPlaybookSlice';
import { removeLandmineAlert, addLandmineAlert } from '../store/slices/landmineSlice';
import { removeCompetitorAlert, addCompetitorAlert } from '../store/slices/competitorSlice';

import TopBar from '../components/TopBar';
import ConversationFeed from '../components/ConversationFeed';
import AudioCaptureModal from '../components/AudioCaptureModal';
import SalesPlaybookPanel from '../components/SalesPlaybookPanel';
import { LandmineAlertsContainer } from '../components/LandmineAlert';
import CompetitorAlert from '../components/CompetitorAlert';
import SessionStatusBadge, { SessionPhase } from '../components/training/SessionStatusBadge';
import { CombinedInfoBar } from '../components/CombinedInfoBar';

import { TranscriptionResult } from '../services/audioCaptureService';
import { audioCaptureService } from '../services/audioCaptureService';
import { speechRateService } from '../services/speechRateService';
import {
  trainingWebSocketService,
  TrainingItem,
  ItemEvaluation,
  ItemFollowup,
  PlaybookPhaseData,
  PlaybookCoverageUpdate,
  DiscoveryTurnData,
  AllItemsCoveredData,
  PhaseChangedData,
  ConversationPhase,
} from '../services/trainingWebSocketService';
import { playBase64Audio } from '../utils/audioPlayer';
import { generateClientId } from '../utils/clientId';
import { config } from '../config';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SILENCE_THRESHOLD = 0.02;      // amplitude below which we consider silence
const SILENCE_DURATION_MS = 3500;    // ms of sustained silence → auto-submit
const AUTO_READY_DELAY_MS = 2000;    // ms after session_started before sending `ready`
const MANUAL_SUBMIT_TIMEOUT_MS = 10_000; // show emergency manual button after this long

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const TrainingSessionPage: React.FC = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();
  const dispatch = useDispatch();

  const location = useLocation();
  const trackId = new URLSearchParams(location.search).get('trackId');

  const { lastSpeechRateAlert, transcripts, speechRateSettings } = useSelector(
    (state: RootState) => state.conversation
  );
  const { isAuthenticated, user, tokenInfo, isMongoVerified } = useSelector(
    (state: RootState) => state.auth
  );
  const landmineAlerts = useSelector((state: RootState) => state.landmine?.alerts || []);
  const competitorAlerts = useSelector((state: RootState) => state.competitor?.alerts || []);

  // ---------------------------------------------------------------------------
  // UI state
  // ---------------------------------------------------------------------------

  const [clientId, setClientId] = useState<string>('');
  const [showSpeechAlert, setShowSpeechAlert] = useState(false);
  const [isAudioCaptureActive, setIsAudioCaptureActive] = useState(false);
  const [isAudioModalOpen, setIsAudioModalOpen] = useState(false);

  // Session state
  const [isTrainingConnected, setIsTrainingConnected] = useState(false);
  const [sessionPhase, setSessionPhase] = useState<SessionPhase>('waiting');
  const [totalItems, setTotalItems] = useState(0);
  const [completedItems, setCompletedItems] = useState(0);
  const [hasSpokenForCurrentItem, setHasSpokenForCurrentItem] = useState(false);
  const [showEmergencySubmit, setShowEmergencySubmit] = useState(false);

  // Item state
  const [currentTrainingItem, setCurrentTrainingItem] = useState<TrainingItem | null>(null);
  const [currentFollowupRound, setCurrentFollowupRound] = useState(0);

  // Playbook state
  const [hasPlaybook, setHasPlaybook] = useState(false);
  const [currentPlaybookPhase, setCurrentPlaybookPhase] = useState<string | null>(null);

  // All items covered banner
  const [allItemsCovered, setAllItemsCovered] = useState(false);

  // Conversation phase machine — server-driven banner showing which phase
  // the AI customer is in (kb_phase / playbook_phase / close_phase / free_chat).
  const [conversationPhase, setConversationPhase] = useState<ConversationPhase | null>(null);
  // True once the backend has fully wired up the conversation manager and
  // pushed the initial `phase_changed` event. Used to gate the decibel
  // meter / "session live" UI behind a loader, so the user only sees
  // "live" once the pipeline truly is.
  const [isSessionReady, setIsSessionReady] = useState(false);

  // Completion
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completionResult, setCompletionResult] = useState<any>(null);

  // ---------------------------------------------------------------------------
  // Refs (avoid stale closures in callbacks)
  // ---------------------------------------------------------------------------

  const isTrainingConnectedRef = useRef(false);
  const currentTrainingItemRef = useRef<TrainingItem | null>(null);
  const sessionPhaseRef = useRef<SessionPhase>('waiting');
  const hasSpokenRef = useRef(false);
  const isPlaybookPhaseActiveRef = useRef(false);

  // Silence detection
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const userHasSpokenRef = useRef(false);   // set true on first salesperson speech per item

  // Emergency manual-submit timeout
  const emergencyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const updatePhase = useCallback((phase: SessionPhase) => {
    setSessionPhase(phase);
    sessionPhaseRef.current = phase;
  }, []);

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current !== null) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  const clearEmergencyTimer = useCallback(() => {
    if (emergencyTimerRef.current !== null) {
      clearTimeout(emergencyTimerRef.current);
      emergencyTimerRef.current = null;
    }
    setShowEmergencySubmit(false);
  }, []);

  const stopEverything = useCallback(() => {
    audioCaptureService.stopCapture();
    setIsAudioCaptureActive(false);
    setIsSessionReady(false);
    trainingWebSocketService.disconnect();
    isTrainingConnectedRef.current = false;
    setIsTrainingConnected(false);
    setCurrentTrainingItem(null);
    currentTrainingItemRef.current = null;
    clearSilenceTimer();
    clearEmergencyTimer();
    // Wipe the conversation feed so the next session doesn't inherit
    // question_detected / stored_answer entries from this one.
    dispatch(clearConversation());
  }, [clearSilenceTimer, clearEmergencyTimer, dispatch]);

  // ---------------------------------------------------------------------------
  // Submit logic (used by both silence detection and manual submit)
  // ---------------------------------------------------------------------------

  const handleAutoSubmit = useCallback(() => {
    clearSilenceTimer();
    clearEmergencyTimer();

    const item = currentTrainingItemRef.current;
    // item may be null for qualifying turns — that's fine, server handles empty trackItemId

    if (isPlaybookPhaseActiveRef.current && currentPlaybookPhase) {
      updatePhase('processing');
      trainingWebSocketService.submitPlaybookResponse(
        currentPlaybookPhase,
        '',   // accumulated transcript is tracked server-side
        0
      );
    } else if (currentFollowupRound > 0) {
      updatePhase('evaluating');
      trainingWebSocketService.submitFollowupResponse(item?.trackItemId ?? '', '', 0);
    } else {
      updatePhase('evaluating');
      trainingWebSocketService.submitAndNext(item?.trackItemId ?? '', '', 0);
    }

    hasSpokenRef.current = false;
    userHasSpokenRef.current = false;
    setHasSpokenForCurrentItem(false);
  }, [clearSilenceTimer, clearEmergencyTimer, currentFollowupRound, currentPlaybookPhase, updatePhase]);

  // ---------------------------------------------------------------------------
  // Silence detection — called from onAudioLevel
  // ---------------------------------------------------------------------------

  const handleAudioLevel = useCallback(
    (level: number) => {
      const phase = sessionPhaseRef.current;
      if (phase !== 'recording') return;

      if (level > SILENCE_THRESHOLD) {
        // User is speaking — cancel any pending silence timer
        clearSilenceTimer();
        userHasSpokenRef.current = true;
        return;
      }

      // Below threshold — only start silence timer after user has spoken at least once
      if (!userHasSpokenRef.current) return;
      if (silenceTimerRef.current !== null) return;  // timer already running

      silenceTimerRef.current = setTimeout(() => {
        silenceTimerRef.current = null;
        if (sessionPhaseRef.current === 'recording' && userHasSpokenRef.current) {
          handleAutoSubmit();
        }
      }, SILENCE_DURATION_MS);
    },
    [clearSilenceTimer, handleAutoSubmit]
  );

  // ---------------------------------------------------------------------------
  // Session initialization
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const initializeSession = async () => {
      const newClientId = generateClientId();
      setClientId(newClientId);

      if (sessionId && isAuthenticated && tokenInfo?.token) {
        try {
          const { apiService } = await import('../services/apiService');
          const response = await apiService.get(
            `/training/sessions/check/${sessionId}?_t=${Date.now()}`
          );
          if (response.success && (response as any).exists === true) {
            window.alert(
              'This training session has already ended. Please start a new training session from the admin portal.'
            );
            const auth = getAuth();
            await signOut(auth);
            dispatch(logoutUser() as any);
            navigate('/login');
            return;
          }
        } catch {
          // Continue if API check fails — better to allow than block incorrectly
        }

        // Load track data to pre-populate playbook before session starts
        if (trackId) {
          try {
            const { apiService } = await import('../services/apiService');
            const trackResponse = await apiService.get(`/training/tracks/${trackId}`);
            if (trackResponse.success && trackResponse.data) {
              const track = trackResponse.data;
              const playbookConfig = track.playbookConfig;
              if (playbookConfig?.enabled && playbookConfig?.industry && playbookConfig?.roleName) {
                setHasPlaybook(true);
                const industriesResult = await dispatch(fetchIndustries() as any).unwrap();
                const industry = industriesResult?.find((i: any) => i.industry === playbookConfig.industry);
                if (industry) {
                  dispatch(setSelectedIndustry(industry));
                  const role = industry.roles?.find((r: any) => r.name === playbookConfig.roleName);
                  if (role) {
                    dispatch(setSelectedRole(role));
                    dispatch(fetchRoleData({ industry: playbookConfig.industry, roleName: playbookConfig.roleName }) as any);
                  }
                }
              }
              if (track.itemCount || track.items?.length) {
                setTotalItems(track.itemCount || track.items.length);
              }
            }
          } catch {
            // Non-fatal — playbook will load when session starts as fallback
          }
        }
      }

      if (sessionId) {
        import('../services/sessionManager').then(({ sessionManager }) => {
          sessionManager.forceNewSession(sessionId);
        });
      }
    };

    initializeSession();
  }, [sessionId, isAuthenticated, tokenInfo, dispatch, navigate, trackId]);

  // Auth guard
  useEffect(() => {
    if (!isAuthenticated || !tokenInfo?.token || !isMongoVerified) {
      navigate('/login');
    }
  }, [isAuthenticated, tokenInfo, isMongoVerified, navigate]);

  // Cleanup on tab close
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isAudioCaptureActive) audioCaptureService.stopCapture();
      if (isTrainingConnected) trainingWebSocketService.disconnect();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isAudioCaptureActive, isTrainingConnected]);

  // ---------------------------------------------------------------------------
  // WebSocket event handlers — registered once on mount
  // ---------------------------------------------------------------------------

  useEffect(() => {
    trainingWebSocketService.onSessionStarted(async (data) => {
      isTrainingConnectedRef.current = true;
      setIsTrainingConnected(true);
      setTotalItems(data.itemCount ?? 0);
      setCompletedItems(0);
      setHasPlaybook(data.hasPlaybook ?? false);
      // Rep speaks first — go directly to recording phase
      updatePhase('recording');

      // Auto-populate playbook if enabled — load industries, set selection, fetch role data
      if (data.hasPlaybook && data.playbookIndustry && data.playbookRoleName) {
        try {
          const industriesResult = await dispatch(fetchIndustries() as any).unwrap();
          const industry = industriesResult?.find((i: any) => i.industry === data.playbookIndustry);
          if (industry) {
            dispatch(setSelectedIndustry(industry));
            const role = industry.roles?.find((r: any) => r.name === data.playbookRoleName);
            if (role) {
              dispatch(setSelectedRole(role));
              dispatch(fetchRoleData({ industry: data.playbookIndustry, roleName: data.playbookRoleName }) as any);
            }
          }
        } catch (err) {
          console.error('TrainingSessionPage: failed to load playbook data', err);
        }
      }

      // Auto-deliver first item after short delay
      setTimeout(() => {
        if (isTrainingConnectedRef.current) {
          trainingWebSocketService.sendReady();
        }
      }, AUTO_READY_DELAY_MS);
    });

    trainingWebSocketService.onItemEvaluated(async (evaluation) => {
      setCompletedItems((prev) => prev + 1);
      setCurrentFollowupRound(0);
      clearSilenceTimer();
      clearEmergencyTimer();
      userHasSpokenRef.current = false;
      setHasSpokenForCurrentItem(false);

      dispatchDetectedObjections(evaluation.detectedObjections ?? [], '');

      if (evaluation.aiFollowUp) {
        dispatch(addTranscript({ text: evaluation.aiFollowUp, speaker: 'customer', timestamp: new Date().toISOString() }));
      }
      if (evaluation.aiFollowUpAudioBase64) {
        try { await playBase64Audio(evaluation.aiFollowUpAudioBase64); } catch { /* non-fatal */ }
      }

      // Stay in 'processing' until next discovery_turn or playbook_phase arrives
      // (avoids briefly flashing "Ready" while the server generates the next turn)
      updatePhase('processing');
    });

    trainingWebSocketService.onItemFollowup(async (followup) => {
      setCurrentFollowupRound(followup.round);
      clearSilenceTimer();
      userHasSpokenRef.current = false;
      setHasSpokenForCurrentItem(false);

      dispatch(addTranscript({ text: followup.aiText, speaker: 'customer', timestamp: new Date().toISOString() }));

      if (followup.aiAudioBase64) {
        try { await playBase64Audio(followup.aiAudioBase64); } catch { /* non-fatal */ }
      }

      updatePhase('recording');
    });

    trainingWebSocketService.onDiscoveryTurn(async (data: DiscoveryTurnData) => {
      // Discovery AI turn: natural conversation from the AI customer.
      // If a KB item is surfaced, show the item card + answers panel.
      // If qualifying-only, it appears in the transcript only.
      updatePhase('delivering');
      userHasSpokenRef.current = false;
      setHasSpokenForCurrentItem(false);
      isPlaybookPhaseActiveRef.current = false;
      clearSilenceTimer();
      clearEmergencyTimer();

      dispatch(addTranscript({ text: data.aiText, speaker: 'customer', timestamp: new Date().toISOString() }));

      if (data.surfacedItem) {
        const surfaced = data.surfacedItem;

        // NOTE: Objection/FAQ ConversationFeed dispatch used to happen here
        // using `data.aiText` as the question key. It now happens via the
        // dedicated `question_detected` + `stored_answer_generated` WS
        // events emitted by the backend with the canonical question text
        // from contentSnapshot.questions[0] — see the onQuestionDetected /
        // onStoredAnswerGenerated handlers below. Single source of truth.

        // Dispatch detection alerts
        dispatchDetectedObjections(surfaced.detectedObjections ?? [], data.aiText);

        // Build a TrainingItem so the current item ref is set for silence detection / submit routing
        const syntheticItem: TrainingItem & { deliveredAt: number } = {
          trackItemId: surfaced.trackItemId,
          itemType: surfaced.itemType,
          aiText: data.aiText,
          aiAudioBase64: data.aiAudioBase64,
          expectedKeywords: surfaced.expectedKeywords ?? [],
          allowedResponseTime: 60,
          detectedObjections: surfaced.detectedObjections ?? [],
          storedAnswer: surfaced.storedAnswer,
          aiGeneratedAnswer: surfaced.aiGeneratedAnswer,
          deliveredAt: Date.now(),
        };
        setCurrentTrainingItem(syntheticItem);
        currentTrainingItemRef.current = syntheticItem;
      } else {
        // Qualifying/conversational turn — no item card needed
        setCurrentTrainingItem(null);
        currentTrainingItemRef.current = null;
      }

      if (data.aiAudioBase64) {
        try { await playBase64Audio(data.aiAudioBase64); } catch { /* non-fatal */ }
      }

      updatePhase('recording');

      // Start emergency manual submit timer
      emergencyTimerRef.current = setTimeout(() => {
        setShowEmergencySubmit(true);
      }, MANUAL_SUBMIT_TIMEOUT_MS);
    });

    trainingWebSocketService.onPlaybookPhaseStarted(async (data: PlaybookPhaseData) => {
      setCurrentPlaybookPhase(data.phase);
      isPlaybookPhaseActiveRef.current = true;
      updatePhase('playbook_phase');
      userHasSpokenRef.current = false;
      setHasSpokenForCurrentItem(false);
      clearSilenceTimer();
      clearEmergencyTimer();

      dispatch(addTranscript({ text: data.aiText, speaker: 'customer', timestamp: new Date().toISOString() }));

      if (data.aiAudioBase64) {
        try { await playBase64Audio(data.aiAudioBase64); } catch { /* non-fatal */ }
      }

      updatePhase('recording');
    });

    trainingWebSocketService.onPlaybookCoverageUpdate((data: PlaybookCoverageUpdate) => {
      // Reflect coverage in the playbook panel via Redux
      // Map covered points to existing toggleQuestionAsked / togglePitchChecked actions
      // The precise mapping depends on phase; we dispatch a generic signal here.
      // The SalesPlaybookPanel will react to its own Redux state.
      if (data.phase === 'qualifying') {
        data.coveredPoints.forEach((point) => {
          dispatch(toggleQuestionAsked(point) as any);
        });
      } else if (data.phase === 'intro' || data.phase === 'deal_progression') {
        data.coveredPoints.forEach((point) => {
          dispatch(togglePitchChecked(point) as any);
        });
      }
    });

    trainingWebSocketService.onPlaybookPhaseCompleted((_data) => {
      isPlaybookPhaseActiveRef.current = false;
      setCurrentPlaybookPhase(null);
      // Stay in 'processing' until the next phase/turn arrives
      updatePhase('processing');
    });

    trainingWebSocketService.onAllItemsCovered((_data: AllItemsCoveredData) => {
      setAllItemsCovered(true);
    });

    // Phase machine banner — server pushes the initial phase at turn 0 and
    // every transition thereafter (kb → playbook → close).
    trainingWebSocketService.onPhaseChanged((data: PhaseChangedData) => {
      setConversationPhase(data.new_phase);
      // First phase_changed (old_phase=null) marks the end of backend
      // startup — track loaded, items fetched, company context loaded,
      // saver connected, ConversationManager wired. Safe to flip the
      // UI from "starting…" to "live".
      setIsSessionReady(true);
    });

    // ── ConversationFeed: objection/FAQ "detected" events ──────────────
    // Training emits these locally (no production_final_server roundtrip)
    // whenever the AI customer surfaces an Objection/FAQ KB item. The
    // payload shape mirrors the live-call event so the existing Redux
    // slice + ConversationEntry component render it unchanged.
    trainingWebSocketService.onQuestionDetected((data) => {
      dispatch(addQuestion({
        question: data.question,
        category: data.category,
        relevance_score: data.relevance_score,
        timestamp: data.timestamp,
      }));
    });

    trainingWebSocketService.onStoredAnswerGenerated((data) => {
      dispatch(addStoredAnswer({
        question: data.question,
        stored_answer: data.stored_answer,
        timestamp: data.timestamp,
      }));
      // Training intentionally has no AI-generated alternative answer.
      // Dispatch `addAnswer` with the same canonical text so the entry
      // transitions from `pending` → `completed` (otherwise the AI
      // Answer tab in ConversationEntry would show a perpetual
      // "AI is processing…" spinner). Both tabs end up showing the
      // canonical KB answer, which is the UX we want for training.
      dispatch(addAnswer({
        question: data.question,
        answer: data.stored_answer,
        timestamp: data.timestamp,
      }));
    });

    // ── Live landmine / competitor alerts ──────────────────────────────
    // Emitted by training_simulation.run_detections for both speakers.
    // Payload is the raw detection dict from the detection service, which
    // matches the live-call shape — dispatch directly into the same
    // slices the ConversationPage uses so LandmineAlertsContainer /
    // CompetitorAlert render unchanged.
    trainingWebSocketService.onLandmineAlert((data) => {
      dispatch(addLandmineAlert({
        ...data,
        landmine_type: data.landmine_type || 'legal_risk',
        can_say: data.can_say || [],
        cannot_say: data.cannot_say || [],
      }));
    });

    trainingWebSocketService.onCompetitorAlert((data) => {
      dispatch(addCompetitorAlert({
        ...data,
        advantages: data.advantages || [],
        weaknesses: data.weaknesses || [],
        talking_points: data.talking_points || '',
      }));
    });

    trainingWebSocketService.onSessionCompleted((result) => {
      setIsAudioCaptureActive(false);
      audioCaptureService.stopCapture();
      setCompletionResult(result);
      setShowCompletionModal(true);
      updatePhase('completed');
    });

    trainingWebSocketService.onError((error) => {
      console.error('TrainingSessionPage: training error', error);
    });

    trainingWebSocketService.onConnectionChange((connected) => {
      setIsTrainingConnected(connected);
      isTrainingConnectedRef.current = connected;
    });

    return () => {
      trainingWebSocketService.disconnect();
      clearSilenceTimer();
      clearEmergencyTimer();
    };
  }, [dispatch, updatePhase, clearSilenceTimer, clearEmergencyTimer]);

  // ---------------------------------------------------------------------------
  // Speech rate monitoring
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!speechRateSettings.enabled || !isAudioCaptureActive || !isAuthenticated || !tokenInfo?.token) {
      return;
    }
    speechRateService.startMonitoring(
      () => transcripts,
      () => ({ time_window: speechRateSettings.time_window, max_words: speechRateSettings.max_words }),
      (analysis) => dispatch(updateSpeechRateAnalysis(analysis)),
      2000
    );
    return () => speechRateService.stopMonitoring();
  }, [
    speechRateSettings.enabled,
    speechRateSettings.time_window,
    speechRateSettings.max_words,
    transcripts,
    dispatch,
    isAudioCaptureActive,
    isAuthenticated,
    tokenInfo,
  ]);

  // Speech rate alert auto-dismiss
  useEffect(() => {
    if (!lastSpeechRateAlert) return;
    setShowSpeechAlert(true);
    const timer = setTimeout(() => {
      setShowSpeechAlert(false);
      dispatch(clearSpeechRateAlert());
    }, 7000);
    return () => clearTimeout(timer);
  }, [lastSpeechRateAlert, dispatch]);

  // ---------------------------------------------------------------------------
  // Transcript handler
  // ---------------------------------------------------------------------------

  const handleTranscription = useCallback(
    (result: TranscriptionResult) => {
      if (!isAuthenticated || !tokenInfo?.token) return;

      dispatch(addTranscript({
        text: result.transcript,
        speaker: result.speaker || 'salesperson',
        timestamp: result.timestamp.toISOString(),
      }));

      if (result.isFinal && result.speaker === 'salesperson') {
        hasSpokenRef.current = true;
        userHasSpokenRef.current = true;
        setHasSpokenForCurrentItem(true);

        if (sessionPhaseRef.current === 'delivering') {
          updatePhase('recording');
        }

        // Accumulate chunk server-side; actual submission triggered by silence detection
        if (isTrainingConnectedRef.current) {
          trainingWebSocketService.sendMessage({
            type: 'submit_response',
            data: {
              trackItemId: currentTrainingItemRef.current?.trackItemId ?? '',
              userResponse: result.transcript,
              responseTime: 0,
            },
          });
        }
      }
    },
    [isAuthenticated, tokenInfo, dispatch, updatePhase]
  );

  // ---------------------------------------------------------------------------
  // Alert / objection dispatch helper
  // ---------------------------------------------------------------------------

  const dispatchDetectedObjections = (
    objections: Array<any>,
    sourceText: string
  ) => {
    objections.forEach((objection) => {
      if (objection.type === 'landmine') {
        dispatch(addLandmineAlert({
          trigger: objection.trigger || 'Landmine Topic',
          matched_text: sourceText || objection.trigger,
          matched_phrase: objection.trigger || 'Landmine Topic',
          similarity_score: 1.0,
          semantic_match: false,
          landmine_type: objection.landmine_type || 'legal_risk',
          category: objection.category || 'Landmine',
          priority: 'high',
          can_say: objection.can_say || [],
          cannot_say: objection.cannot_say || [],
          quick_tips: objection.quick_tips || '',
          detected_at: new Date().toISOString(),
          timestamp: new Date().toISOString(),
          speaker: sourceText ? 'customer' : 'salesperson',
        }));
      } else if (objection.type === 'competitor') {
        dispatch(addCompetitorAlert({
          competitor: objection.competitor || 'Competitor',
          matched_text: sourceText || `Mentioned: ${objection.competitor}`,
          confidence: 1.0,
          match_type: sourceText ? 'customer_mention' : 'salesperson_mention',
          advantages: objection.advantages || [],
          weaknesses: objection.weaknesses || [],
          talking_points: objection.talking_points || '',
          category: objection.category || 'Competitor',
          detected_at: new Date().toISOString(),
          timestamp: new Date().toISOString(),
          speaker: sourceText ? 'customer' : 'salesperson',
        }));
      }
    });
  };

  // ---------------------------------------------------------------------------
  // Manual control handlers
  // ---------------------------------------------------------------------------

  const handleStartTrainingConnection = () => {
    if (isTrainingConnected || !trackId || !isAuthenticated || !user?.uid) return;
    // Fresh start — drop any leftover conversation entries from a prior
    // session so the new session's question_detected events render cleanly.
    dispatch(clearConversation());
    updatePhase('connecting');
    trainingWebSocketService
      .connect(trackId, sessionId)
      .then(() => {
        isTrainingConnectedRef.current = true;
        setIsTrainingConnected(true);
      })
      .catch(() => {
        isTrainingConnectedRef.current = false;
        setIsTrainingConnected(false);
        updatePhase('waiting');
      });
  };

  const handleManualSubmit = () => {
    if (!hasSpokenRef.current) return;
    handleAutoSubmit();
  };

  const handleFinishSession = () => {
    updatePhase('completed');
    trainingWebSocketService.sendMessage({ type: 'finish_session', data: {} });
  };

  const handleStopEarly = () => {
    if (!window.confirm('Are you sure you want to stop the training session early?')) return;
    // Disconnect triggers session_abandoned on the backend (not session_completed)
    stopEverything();
    updatePhase('completed');
    // Show stopped-early modal with partial results
    setCompletionResult({
      itemsCompleted: completedItems,
      totalItems: totalItems,
      stoppedEarly: true,
    });
    setShowCompletionModal(true);
  };

  const handleSignOut = async () => {
    try {
      dispatch(clearConversation());
      dispatch(clearSalesPlaybookSession());
      const auth = getAuth();
      await signOut(auth);
      dispatch(logoutUser() as any);
      navigate('/login');
    } catch {
      navigate('/login');
    }
  };

  const dismissSpeechAlert = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    e?.stopPropagation();
    setShowSpeechAlert(false);
    dispatch(clearSpeechRateAlert());
  };

  // ---------------------------------------------------------------------------
  // Derived display values
  // ---------------------------------------------------------------------------

  const progressLabel = totalItems > 0 ? `${completedItems} / ${totalItems}` : undefined;

  // Fixed top area = TopBar (48) + CombinedInfoBar live transcript (~56)
  // + optional speech-rate alert (~48 when shown). Without including the
  // transcript bar height the main content was being clipped behind it.
  const TOP_BAR_HEIGHT = 48;
  const TRANSCRIPT_BAR_HEIGHT = 56;
  const SPEECH_ALERT_HEIGHT = 48;
  const fixedHeaderHeight =
    TOP_BAR_HEIGHT + TRANSCRIPT_BAR_HEIGHT + (showSpeechAlert ? SPEECH_ALERT_HEIGHT : 0);

  // ---------------------------------------------------------------------------
  // Guard — do not render unless authenticated
  // ---------------------------------------------------------------------------

  if (!isAuthenticated || !tokenInfo?.token || !isMongoVerified) return null;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
      {/* ------------------------------------------------------------------ */}
      {/* Fixed top section                                                   */}
      {/* ------------------------------------------------------------------ */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <TopBar onSignOut={handleSignOut} clientId={clientId} />

        {/* Live transcript bar — same component as ConversationPage */}
        <CombinedInfoBar />

        {/* Speech rate alert */}
        {showSpeechAlert && lastSpeechRateAlert && (
          <div className="bg-red-500 text-white px-6 py-3 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div>
                <span className="font-bold">SPEAKING TOO FAST!</span>
                <span className="ml-2">
                  {lastSpeechRateAlert.total_words}/{lastSpeechRateAlert.max_words} words
                  in {lastSpeechRateAlert.time_window}s — Please slow down
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={dismissSpeechAlert}
              className="bg-red-600 hover:bg-red-700 px-3 py-1 rounded text-sm transition-colors"
            >
              Dismiss
            </button>
          </div>
        )}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Alert overlays                                                      */}
      {/* ------------------------------------------------------------------ */}
      <LandmineAlertsContainer
        alerts={landmineAlerts}
        onDismiss={(index) => dispatch(removeLandmineAlert(index))}
      />
      <div className="fixed top-32 right-4 z-40 space-y-2">
        {competitorAlerts.map((alert: any, index: number) => (
          <CompetitorAlert
            key={`competitor-alert-${index}`}
            alert={alert}
            onDismiss={() => dispatch(removeCompetitorAlert(index))}
          />
        ))}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* All items covered banner — persistent, non-dismissable              */}
      {/* ------------------------------------------------------------------ */}
      {allItemsCovered && !showCompletionModal && (
        <div className="fixed top-16 left-0 right-0 z-45 bg-green-600 text-white px-6 py-3 flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-2">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
            <span className="font-medium">
              All knowledge base items have been covered. You can continue the conversation or end the session.
            </span>
          </div>
          <button
            type="button"
            onClick={() => {
              trainingWebSocketService.endSession();
            }}
            className="px-4 py-1.5 bg-white text-green-700 rounded-lg font-medium text-sm hover:bg-green-50 transition-colors"
          >
            End Session
          </button>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Main content                                                        */}
      {/* ------------------------------------------------------------------ */}
      <div
        className="flex-1 flex min-h-0 gap-4 px-4 pt-2 overflow-hidden"
        style={{ paddingTop: fixedHeaderHeight + 8 /* header + small spacing (phase chip moved into status panel) */ }}
      >
        {/* -------------------------------------------------------- */}
        {/* Left column (40%)                                        */}
        {/* -------------------------------------------------------- */}
        <div className="w-2/5 flex flex-col min-h-0 gap-3 pb-10">
          {/* Status panel — fixed height */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex-shrink-0">
            {/* Compact phase indicator (left) + recording status badge (right).
                Replaces the redundant "Training Session" header — the page
                title already tells the user where they are. */}
            <div className="flex items-center justify-between mb-3 gap-2">
              {conversationPhase ? (
                <div className="flex items-center gap-2 min-w-0">
                  {conversationPhase === 'kb_phase' && (
                    <>
                      <span className="inline-block w-2 h-2 rounded-full bg-blue-500 animate-pulse flex-shrink-0" />
                      <span className="text-sm font-medium text-slate-700 truncate">
                        Knowledge Base{totalItems > 0 ? ` (${completedItems}/${totalItems})` : ''}
                      </span>
                    </>
                  )}
                  {conversationPhase === 'playbook_phase' && (
                    <>
                      <span className="inline-block w-2 h-2 rounded-full bg-purple-500 flex-shrink-0" />
                      <span className="text-sm font-medium text-slate-700 truncate">Sales Playbook</span>
                    </>
                  )}
                  {conversationPhase === 'close_phase' && (
                    <>
                      <span className="inline-block w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                      <span className="text-sm font-medium text-slate-700 truncate">Closing</span>
                    </>
                  )}
                  {conversationPhase === 'free_chat' && (
                    <>
                      <span className="inline-block w-2 h-2 rounded-full bg-slate-400 flex-shrink-0" />
                      <span className="text-sm font-medium text-slate-700 truncate">Open Conversation</span>
                    </>
                  )}
                </div>
              ) : (
                <span className="text-sm font-medium text-slate-500">Ready</span>
              )}
              <SessionStatusBadge
                phase={sessionPhase}
                progressLabel={progressLabel}
              />
            </div>

            {/* Audio capture control */}
            {!isAudioCaptureActive && (
              <button
                type="button"
                onClick={() => setIsAudioModalOpen(true)}
                className="px-4 py-2 rounded-lg font-medium bg-blue-500 hover:bg-blue-600 text-white text-sm transition-colors"
              >
                Start Audio Capture
              </button>
            )}

            {/* Starting-up loader — bridges the gap between "audio capture
                started" and "backend conversation manager truly ready".
                The decibel meter / session-live UI stays hidden until the
                first phase_changed event arrives (see isSessionReady). */}
            {isAudioCaptureActive && !isSessionReady && (
              <div className="flex items-center gap-3 mt-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200">
                <svg
                  className="animate-spin h-5 w-5 text-blue-600 flex-shrink-0"
                  xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
                <div className="text-sm text-blue-900">
                  <div className="font-medium">Starting session…</div>
                  <div className="text-xs text-blue-700">
                    Connecting audio pipeline and loading training context.
                  </div>
                </div>
              </div>
            )}

            {/* Session controls */}
            {isAudioCaptureActive && isSessionReady && (
              <div className="flex items-center gap-2 mt-2">
                {isTrainingConnected && sessionPhase === 'recording' && showEmergencySubmit && (
                  <button
                    type="button"
                    onClick={handleManualSubmit}
                    disabled={!hasSpokenForCurrentItem}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      hasSpokenForCurrentItem
                        ? 'bg-indigo-500 hover:bg-indigo-600 text-white'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Submit Manually
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleStopEarly}
                  className="px-4 py-2 rounded-lg font-medium bg-red-500 hover:bg-red-600 text-white text-sm transition-colors"
                >
                  Stop Early
                </button>
              </div>
            )}
          </div>

          {/* Playbook panel — stretches to fill remaining height */}
          {hasPlaybook && (
            <div className="flex-1 min-h-0">
              <SalesPlaybookPanel
                isSessionActive={isAudioCaptureActive && isTrainingConnected}
                hideSelectors
                fillHeight
                onPlaybookAction={(action) => {
                  trainingWebSocketService.sendMessage({
                    type: 'playbook_action',
                    data: action,
                  });
                }}
              />
            </div>
          )}

          {/* Audio capture modal (rendered inline so it stays accessible) */}
          <AudioCaptureModal
            isOpen={isAudioModalOpen}
            onClose={() => setIsAudioModalOpen(false)}
            onTranscription={handleTranscription}
            onCaptureStateChange={(isCapturing) => {
              setIsAudioCaptureActive(isCapturing);
              if (!isCapturing) setIsSessionReady(false);
              if (!isCapturing && isTrainingConnected) {
                trainingWebSocketService.disconnect();
                isTrainingConnectedRef.current = false;
                setIsTrainingConnected(false);
                setCurrentTrainingItem(null);
                currentTrainingItemRef.current = null;
                clearSilenceTimer();
                clearEmergencyTimer();
              }
            }}
            onAudioLevel={handleAudioLevel}
            isTrainingConnected={isTrainingConnected}
            waitForTrainingConnection={true}
            onStartTrainingConnection={handleStartTrainingConnection}
            isSessionReady={isSessionReady}
          />
        </div>

        {/* -------------------------------------------------------- */}
        {/* Right column (60%)                                       */}
        {/* -------------------------------------------------------- */}
        <div className="w-3/5 flex flex-col min-h-0 pb-10">
          {/* Conversation feed (detected objections, FAQs, answers) */}
          <div className="flex-1 flex flex-col min-h-0">
            <ConversationFeed />
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Status footer                                                       */}
      {/* ------------------------------------------------------------------ */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-2 z-40">
        <div className="text-center text-xs text-gray-400">
          Lushin Training — Session: {sessionId}
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Completion modal                                                    */}
      {/* ------------------------------------------------------------------ */}
      {showCompletionModal && completionResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <div className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-4 ${
                completionResult?.stoppedEarly ? 'bg-yellow-100' : 'bg-green-100'
              }`}>
                {completionResult?.stoppedEarly ? (
                  <svg className="h-10 w-10 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <svg className="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {completionResult?.stoppedEarly ? 'Session Stopped Early' : 'Training Session Complete!'}
              </h3>

              {!completionResult?.stoppedEarly && (
                <div className="mb-6">
                  <div className="text-5xl font-bold text-blue-600 mb-2">
                    {(() => {
                      const done = completionResult?.itemsCompleted ?? completedItems;
                      const total = completionResult?.totalItems ?? totalItems;
                      return total > 0 ? Math.round((done / total) * 100) : 0;
                    })()}%
                  </div>
                  <p className="text-gray-600">Knowledge Base Completed</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                <div className="bg-gray-50 rounded p-3">
                  <div className="font-semibold text-gray-700">Items Completed</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {completionResult?.itemsCompleted ?? completedItems}
                  </div>
                </div>
                <div className="bg-gray-50 rounded p-3">
                  <div className="font-semibold text-gray-700">Total Items</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {completionResult?.totalItems ?? totalItems}
                  </div>
                </div>
              </div>

              <p className="text-gray-600 mb-6">
                {completionResult?.stoppedEarly
                  ? 'Your partial session has been saved. You can review what was completed or start a new session.'
                  : 'Your session has been saved. Deep analysis will be ready shortly.'}
              </p>

              <button
                type="button"
                onClick={() => {
                  window.location.href = `${config.server.adminPortalUrl}/training`;
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                {completionResult?.stoppedEarly ? 'Back to Training' : 'View Detailed Results'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingSessionPage;
